<template>
  <div class="h-full overflow-auto" :class="$style['hero-background']">
    <PublicLandingHeader
      :title="appSettingsStore.applicationTitle"
      subtitle="Clark County, Nevada Department of Environment and Sustainability"
    />

    <PublicLandingContainer class="grid justify-start gap-16 xl:grid-cols-2 2xl:grid-cols-3">
      <PublicLandingBackdrop class="grid grid-cols-1 gap-10 2xl:col-span-2 2xl:grid-cols-2" light>
        <PublicLandingOnlineServices
          class="2xl:col-span-2"
          title="Online Services"
          body="The Air Quality Management System (AQMS) offers online access to Division of Air Quality services."
        ></PublicLandingOnlineServices>

        <PublicLandingCard>
          <PublicLandingCardSection label="Stationary Source Permitting">
            <PublicLandingParagraph>
              Apply online for a new major source operating permit, authority to construct, or a minor source permit, or
              manage existing permits for a stationary source.
            </PublicLandingParagraph>
            <PublicLandingList>
              <PublicLandingListItem label="Apply for a new permit or authority to construct" />
              <PublicLandingListItem label="Modify or renew your existing permit" />
              <PublicLandingListItem label="Request a transfer of ownership or change of name" />
              <PublicLandingListItem label="Submit a portable source permit move notice" />
            </PublicLandingList>
          </PublicLandingCardSection>

          <PublicLandingCardSection label="Stationary Source Compliance">
            <PublicLandingParagraph>
              Submit and track stationary source compliance reporting forms online.
            </PublicLandingParagraph>
            <PublicLandingList>
              <PublicLandingListItem label="Annual emissions inventory forms" />
              <PublicLandingListItem label="Compliance certification forms" />
              <PublicLandingListItem label="24-hour excess emission forms" />
              <PublicLandingListItem label="72-hour deviation reporting forms" />
              <PublicLandingListItem label="Visible emissions evaluation forms" />
            </PublicLandingList>
          </PublicLandingCardSection>

          <PublicLandingCardSection label="Source Testing">
            <PublicLandingParagraph>
              Submit vapor recovery test notification forms, test result submittal forms, and test result forms for
              gasoline dispensing operations.
            </PublicLandingParagraph>
          </PublicLandingCardSection>

          <PublicLandingCardSection label="Enforcement">
            <PublicLandingParagraph>
              Submit forms to request a hearing before the Air Pollution Control Hearing Board, appeal Hearing Officer
              decisions, and apply for an olive tree exemption.
            </PublicLandingParagraph>
          </PublicLandingCardSection>

          <PublicLandingCardSection label="Asbestos">
            <PublicLandingParagraph>
              Submit original, cancellation, and revision notifications online if your asbestos abatement or demolition
              project involves asbestos-containing materials.
            </PublicLandingParagraph>
          </PublicLandingCardSection>

          <PublicLandingCardSection label="Open Burning">
            <PublicLandingParagraph>
              Submit an open burning application and notification form online if advanced approval is required to burn
              material in an open fire.
            </PublicLandingParagraph>
          </PublicLandingCardSection>
        </PublicLandingCard>
        <PublicLandingCard>
          <PublicLandingCardSection label="Dust Control Permitting">
            <PublicLandingParagraph>
              Apply online for dust control operating permits or manage existing permits at construction projects.
            </PublicLandingParagraph>
            <PublicLandingList>
              <PublicLandingListItem label="Apply for a new permit" />
              <PublicLandingListItem label="Modify or renew your existing permit" />
              <PublicLandingListItem label="Submit a permit closure request" />
            </PublicLandingList>
          </PublicLandingCardSection>

          <PublicLandingCardSection label="Dust Classes">
            <PublicLandingParagraph>
              Register for a public dust class or request a private class for 20 or more attendees.
            </PublicLandingParagraph>
            <PublicLandingList>
              <PublicLandingListItem label="Register yourself or others for a dust class" />
              <PublicLandingListItem label="Reschedule if you are unable to attend a dust class" />
              <PublicLandingListItem label="Access class attendance and download dust cards" />
            </PublicLandingList>
          </PublicLandingCardSection>

          <PublicLandingCardSection label="Dust Monitor Classes">
            <PublicLandingParagraph>Submit a dust monitor class online registration online.</PublicLandingParagraph>
            <PublicLandingList>
              <PublicLandingListItem label="Enroll for a dust monitor class" />
              <PublicLandingListItem
                label="Download payment voucher once ten attendees have registered and a class is scheduled"
              />
              <PublicLandingListItem label="Request that a dust monitor class be scheduled for your company" />
            </PublicLandingList>
          </PublicLandingCardSection>
        </PublicLandingCard>
      </PublicLandingBackdrop>
      <PublicLandingBackdrop class="grid gap-10 self-start" light>
        <PublicLandingCard>
          <PublicLandingCardSection label="Air Quality Complaints">
            <PublicLandingParagraph>
              Complaints about air quality concerns can be submitted online.
            </PublicLandingParagraph>
            <PublicLandingList class="mb-3">
              <PublicLandingListItem label="Excessive dust" />
              <PublicLandingListItem label="Unauthorized burning" />
              <PublicLandingListItem label="Unusual or unpleasant odor" />
              <PublicLandingListItem label="Other air quality concerns" />
            </PublicLandingList>
            <PublicLandingParagraph>
              All complaints will be responded to within 24 hours or the next business day if received on a Friday.
              Include your name and contact details to receive our investigation findings.
            </PublicLandingParagraph>
          </PublicLandingCardSection>
          <BaseButton
            label="File a Complaint"
            variant="priority"
            class="w-full"
            @click="$router.push({ name: routeNames.complaint.new })"
          />
        </PublicLandingCard>
      </PublicLandingBackdrop>
    </PublicLandingContainer>
    <MdrnFooter light black-and-white />
  </div>
</template>

<script lang="ts">
import { InitialBadge } from "@modernary/mui/components/badge";
import { BaseButton } from "@modernary/mui/components/button";
import { MdrnAttribution, MdrnFooter, NoneFoundFlag, useAppSettingsStore } from "@modernary/mui/components/common";
import { LoginButton, LogoutButton, RegisterButton } from "@modernary/mui/utils/authentication/buttons";
import { defineComponent } from "vue";
import { FaceSmileIcon, PlusIcon } from "@heroicons/vue/24/solid";
import { ChevronRightIcon } from "@heroicons/vue/24/outline";
import { PhoneIcon, EnvelopeIcon } from "@heroicons/vue/20/solid";
import {
  PublicLandingBackdrop,
  PublicLandingCard,
  PublicLandingCardSection,
  PublicLandingContainer,
  PublicLandingHeader,
  PublicLandingList,
  PublicLandingListItem,
  PublicLandingOnlineServices,
  PublicLandingParagraph,
} from "@modernary/mui/components/publicLanding";
import { routeNames } from "@/routes";

// Home content for unauth'd users
export default defineComponent({
  name: "HomePublic",
  setup() {
    const appSettingsStore = useAppSettingsStore();

    return { BaseButton, appSettingsStore, routeNames };
  },
  components: {
    BaseButton,
    InitialBadge,
    LoginButton,
    RegisterButton,
    PlusIcon,
    ChevronRightIcon,
    MdrnAttribution,
    FaceSmileIcon,
    NoneFoundFlag,
    PhoneIcon,
    EnvelopeIcon,
    LogoutButton,
    PublicLandingListItem,
    PublicLandingList,
    PublicLandingParagraph,
    PublicLandingCard,
    PublicLandingCardSection,
    MdrnFooter,
    PublicLandingHeader,
    PublicLandingBackdrop,
    PublicLandingContainer,
    PublicLandingOnlineServices,
  },
});
</script>

<style lang="css" module>
.hero-background {
  background-color: #c7bbcb; /* average color from the sky of the bg */
  background-image: url("/images/backgrounds/lv.webp");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/*
  This adds a background color to the hero text to keep it legible on the background image.
  The odd aspect ratio is where the text will start to overlap the busier parts of the image.
*/
.hero-container {
  /* @media (min-aspect-ratio: 1314/515) { */
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  /* } */
}
</style>
